import type { components, paths } from "./schema";
import type { RequestData, ResponseSuccessJSON } from "./utils";

export type Urls = keyof paths;

// ----------------- Shared schema exports -----------------
export type Actions = components["schemas"]["Actions"];
export type AdjustmentPaymentDirection =
	components["schemas"]["AdjustmentPaymentDirection"];
export type AdjustmentPaymentIn = components["schemas"]["AdjustmentPaymentIn"];
export type AdjustmentPaymentOut =
	components["schemas"]["AdjustmentPaymentOut"];
export type DashboardSellerForOrder =
	components["schemas"]["DashboardSellerForOrder"];
export type BackupCodeSchema = components["schemas"]["BackupCodeSchema"];
export type BatchUpdateTransferPlayers =
	components["schemas"]["BatchUpdateTransferPlayers"];
export type CompactProxyOutput = components["schemas"]["CompactProxyOutput"];
export type CompactSenderGroupOutput =
	components["schemas"]["CompactSenderGroupOutput"];
export type CookieAuth = components["schemas"]["CookieAuth"];
export type DashboardCommon = components["schemas"]["DashboardCommon"];
export type DashboardOrderTransferRecord =
	components["schemas"]["DashboardOrderTransferRecord"];
export type DashboardOverview = components["schemas"]["DashboardOverview"];
export type DashboardSeller = components["schemas"]["DashboardSeller"];
export type DashboardSenderTransferRecord =
	components["schemas"]["DashboardSenderTransferRecord"];
export type DashboardUser = components["schemas"]["DashboardUser"];
export type TransferListItem = components["schemas"]["TransferListItem"];
export type GAuthSchema = components["schemas"]["GAuthSchema"];
export type GamingConsoleSettings =
	components["schemas"]["GamingConsoleSettings"];
export type IMAPSchema = components["schemas"]["IMAPSchema"];
export type LoginOutput = components["schemas"]["LoginOutput"];
export type MarketAutoPaymentIn = components["schemas"]["MarketAutoPaymentIn"];
export type MarketAutoPaymentOut =
	components["schemas"]["MarketAutoPaymentOut"];
export type MarketManualPaymentIn =
	components["schemas"]["MarketManualPaymentIn"];
export type MarketManualPaymentOut =
	components["schemas"]["MarketManualPaymentOut"];
export type OrderBuyerOutput = components["schemas"]["OrderBuyerOutput"];
export type OrderStatusSchema = components["schemas"]["OrderStatusSchema"];
export type OrdersFacets = components["schemas"]["OrdersFacets"];
export type OriginStatusCategory =
	components["schemas"]["OriginStatusCategory"];
export type PCSettings = components["schemas"]["PCSettings"];
export type PaymentState = components["schemas"]["PaymentState"];
export type PlayerMetadataSchema =
	components["schemas"]["PlayerMetadataSchema"];
export type ProxyOutput = components["schemas"]["ProxyOutput"];
export type ProxyProtocol = components["schemas"]["ProxyProtocol"];
export type RelationOutputForBuyer =
	components["schemas"]["RelationOutputForBuyer"];
export type RelationOutputForSeller =
	components["schemas"]["RelationOutputForSeller"];
export type RelationTransactionOutput =
	components["schemas"]["RelationTransactionOutput"];
export type RelationQuery = components["schemas"]["RelationQuery"];
export type RelationState = components["schemas"]["RelationState"];
export type SellerOutput = components["schemas"]["SellerOutput"];
export type SenderAction = components["schemas"]["SenderAction"];
export type SenderBatchItem = components["schemas"]["SenderBatchItem"];
export type SenderGroupOutput = components["schemas"]["SenderGroupOutput"];
export type SenderInfoOutput = components["schemas"]["SenderInfoOutput"];
export type SenderPriorityUsage = components["schemas"]["SenderPriorityUsage"];
export type SenderUpdateInput = components["schemas"]["SenderUpdateInput"];
export type AdminUsersFacets = components["schemas"]["AdminUsersFacets"];
export type SendersFacets = components["schemas"]["SendersFacets"];
export type SingleProxy = components["schemas"]["SingleProxy"];
export type SnipeTransferPlayerOutput =
	components["schemas"]["SnipeTransferPlayerOutput"];
export type SnipeTransferPlayerResourceInfo =
	components["schemas"]["SnipeTransferPlayerResourceInfo"];
export type SnipeTransferPlayerSuggestionOutput =
	components["schemas"]["SnipeTransferPlayerSuggestionOutput"];
export type SnipeTransferPlayersFacets =
	components["schemas"]["SnipeTransferPlayersFacets"];
export type SystemAutoPaymentIn = components["schemas"]["SystemAutoPaymentIn"];
export type SystemAutoPaymentOut =
	components["schemas"]["SystemAutoPaymentOut"];
export type SystemManualPaymentIn =
	components["schemas"]["SystemManualPaymentIn"];
export type SystemManualPaymentOut =
	components["schemas"]["SystemManualPaymentOut"];
export type TransferPlayerState = components["schemas"]["TransferPlayerState"];
export type TransferRecordOutput =
	components["schemas"]["TransferRecordOutput"];
export type TransferRecordState = components["schemas"]["TransferRecordState"];
export type UpdateOrderInput = components["schemas"]["UpdateOrderInput"];
export type UserLoginIn = components["schemas"]["UserLoginIn"];
export type UserRoles = components["schemas"]["UserRoles"];
export type UserWithAggInfo = components["schemas"]["UserWithAggInfo"];
export type BannerOutput = components["schemas"]["BannerOutput"];
export type BannerScope = components["schemas"]["BannerScope"];
export type CreateBanner = components["schemas"]["CreateBanner"];
export type UpdateBanner = components["schemas"]["UpdateBanner"];

// ----------------- Per-endpoint request/response types -----------------
export type actionOnOrderInput = RequestData<
	paths["/v1/marketplace/admin/orders/{order_id}"]["post"]
>;
export type actionOnOrderOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/orders/{order_id}"]["post"]
>;

export type getPlayersInput = RequestData<
	paths["/v1/marketplace/admin/players/"]["get"]
>;
export type getPlayersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/players/"]["get"]
>;

export type createSnipeTransferPlayersInBatchInput = RequestData<
	paths["/v1/marketplace/admin/snipe-transfer-players/"]["post"]
>;
export type createSnipeTransferPlayersInBatchOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/snipe-transfer-players/"]["post"]
>;

export type deleteSnipeTransferPlayersInBatchInput = RequestData<
	paths["/v1/marketplace/admin/snipe-transfer-players/delete-many/"]["post"]
>;
export type deleteSnipeTransferPlayersInBatchOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/snipe-transfer-players/delete-many/"]["post"]
>;

export type getPlayerDatabaseInput = RequestData<
	paths["/v1/marketplace/admin/player-database/"]["get"]
>;
export type getPlayerDatabaseOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/player-database/"]["get"]
>;
export type getPlayerDatabaseFacetsInput = RequestData<
	paths["/v1/marketplace/admin/player-database/facets/"]["get"]
>;
export type getPlayerDatabaseFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/player-database/facets/"]["get"]
>;

export type refreshPlayerPricesInput = RequestData<
	paths["/v1/marketplace/admin/players/refresh-prices/"]["post"]
>;
export type refreshPlayerPricesOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/players/refresh-prices/"]["post"]
>;

export type resetSnipeTransferPlayersSuccessRatioInput = RequestData<
	paths["/v1/marketplace/admin/snipe-transfer-players/reset-success-ratio/"]["post"]
>;

export type getStSettingInput = RequestData<
	paths["/v1/marketplace/admin/st-setting/"]["get"]
>;
export type getStSettingOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/st-setting/"]["get"]
>;
export type updateStSettingInput = RequestData<
	paths["/v1/marketplace/admin/st-setting/"]["patch"]
>;
export type updateStSettingOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/st-setting/"]["patch"]
>;

export type getUsersInput = RequestData<
	paths["/v1/marketplace/admin/users/"]["get"]
>;
export type getUsersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/users/"]["get"]
>;

export type getUsersFacetsInput = RequestData<
	paths["/v1/marketplace/admin/users/facets/"]["get"]
>;
export type getUsersFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/users/facets/"]["get"]
>;

export type onboardUserInput = RequestData<
	paths["/v1/marketplace/admin/users/"]["post"]
>;
export type onboardUserOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/users/"]["post"]
>;

export type updateUserInput = RequestData<
	paths["/v1/marketplace/admin/users/{user_id}"]["patch"]
>;
export type updateUserOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/users/{user_id}"]["patch"]
>;

export type adminResetPasswordInput = RequestData<
	paths["/v1/marketplace/admin/reset-password"]["post"]
>;
export type adminResetPasswordOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/reset-password"]["post"]
>;

export type buyerOrdersInput = RequestData<
	paths["/v1/marketplace/dashboard/buyer/orders/"]["get"]
>;
export type buyerOrdersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/buyer/orders/"]["get"]
>;

export type allBuyersInput = RequestData<
	paths["/v1/marketplace/dashboard/buyers/"]["get"]
>;
export type allBuyersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/buyers/"]["get"]
>;

export type getOrderTransferRecordsInput = RequestData<
	paths["/v1/marketplace/dashboard/orders/{order_id}/transfer-records/"]["get"]
>;
export type getOrderTransferRecordsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/orders/{order_id}/transfer-records/"]["get"]
>;

export type overviewPageInput = RequestData<
	paths["/v1/marketplace/dashboard/overview/"]["get"]
>;
export type overviewPageOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/overview/"]["get"]
>;

export type allProxiesInput = RequestData<
	paths["/v1/marketplace/dashboard/proxies/"]["get"]
>;
export type allProxiesOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/proxies/"]["get"]
>;

export type sellersForOrderInput = RequestData<
	paths["/v1/marketplace/dashboard/seller-recommendations/"]["get"]
>;
export type sellersForOrderOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/seller-recommendations/"]["get"]
>;

export type sellerOrdersInput = RequestData<
	paths["/v1/marketplace/dashboard/seller/orders/"]["get"]
>;
export type sellerOrdersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/seller/orders/"]["get"]
>;

export type allSellersInput = RequestData<
	paths["/v1/marketplace/dashboard/sellers/"]["get"]
>;
export type allSellersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/sellers/"]["get"]
>;

export type allSenderGroupNamesInput = RequestData<
	paths["/v1/marketplace/dashboard/sender-group-names/"]["get"]
>;
export type allSenderGroupNamesOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/sender-group-names/"]["get"]
>;

export type getSenderTransferRecordsInput = RequestData<
	paths["/v1/marketplace/dashboard/senders/{sender_id}/transfer-records/"]["get"]
>;
export type getSenderTransferRecordsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/senders/{sender_id}/transfer-records/"]["get"]
>;

export type getUserSettingsInput = RequestData<
	paths["/v1/marketplace/dashboard/settings/{user_id}"]["get"]
>;
export type getUserSettingsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/settings/{user_id}"]["get"]
>;
export type updateUserSettingsInput = RequestData<
	paths["/v1/marketplace/dashboard/settings/{user_id}"]["patch"]
>;
export type updateUserSettingsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/settings/{user_id}"]["patch"]
>;

export type updateUserInfoInput = RequestData<
	paths["/v1/marketplace/dashboard/users/{user_id}"]["patch"]
>;
export type updateUserInfoOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/users/{user_id}"]["patch"]
>;

export type loginInput = RequestData<paths["/v1/marketplace/login"]["post"]>;
export type loginOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/login"]["post"]
>;

export type logoutInput = RequestData<paths["/v1/marketplace/logout"]["post"]>;
export type logoutOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/logout"]["post"]
>;

export type createOrderInput = RequestData<
	paths["/v1/marketplace/orders/"]["post"]
>;
export type createOrderOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/orders/"]["post"]
>;
export type getAllOrdersInput = RequestData<
	paths["/v1/marketplace/orders/"]["get"]
>;
export type getAllOrdersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/orders/"]["get"]
>;

export type getOrdersFacetsInput = RequestData<
	paths["/v1/marketplace/orders/facets/"]["get"]
>;
export type getOrdersFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/orders/facets/"]["get"]
>;

export type getOneOrderInput = RequestData<
	paths["/v1/marketplace/orders/{order_id}"]["get"]
>;
export type getOneOrderOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/orders/{order_id}"]["get"]
>;
export type orderActionInput = RequestData<
	paths["/v1/marketplace/orders/{order_id}"]["post"]
>;
export type orderActionOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/orders/{order_id}"]["post"]
>;
export type updateOrderInput = RequestData<
	paths["/v1/marketplace/orders/{order_id}"]["patch"]
>;
export type updateOrderOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/orders/{order_id}"]["patch"]
>;

export type createPaymentInput = RequestData<
	paths["/v1/marketplace/payments/"]["post"]
>;
export type createPaymentOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/payments/"]["post"]
>;
export type getAllPaymentsInput = RequestData<
	paths["/v1/marketplace/payments/"]["get"]
>;
export type getAllPaymentsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/payments/"]["get"]
>;

export type getEurUsdRateOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/payments/eur-usd-rate/"]["get"]
>;

export type getPaymentsFacetsInput = RequestData<
	paths["/v1/marketplace/payments/facets/"]["get"]
>;
export type getPaymentsFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/payments/facets/"]["get"]
>;

export type actOnPaymentInput = RequestData<
	paths["/v1/marketplace/payments/{payment_id}"]["post"]
>;
export type actOnPaymentOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/payments/{payment_id}"]["post"]
>;

export type batchAddInput = RequestData<
	paths["/v1/marketplace/proxies/"]["post"]
>;
export type batchAddOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/proxies/"]["post"]
>;
export type batchUpdateInput = RequestData<
	paths["/v1/marketplace/proxies/"]["patch"]
>;
export type batchUpdateOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/proxies/"]["patch"]
>;
export type getAllProxiesInput = RequestData<
	paths["/v1/marketplace/proxies/"]["get"]
>;
export type getAllProxiesOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/proxies/"]["get"]
>;

export type deleteMultipleProxiesInput = RequestData<
	paths["/v1/marketplace/proxies/delete-many/"]["post"]
>;
export type deleteMultipleProxiesOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/proxies/delete-many/"]["post"]
>;

export type getProxiesFacetsInput = RequestData<
	paths["/v1/marketplace/proxies/facets/"]["get"]
>;
export type getProxiesFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/proxies/facets/"]["get"]
>;

export type deleteOneInput = RequestData<
	paths["/v1/marketplace/proxies/{proxy_id}"]["delete"]
>;
export type deleteOneOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/proxies/{proxy_id}"]["delete"]
>;
export type updateOneInput = RequestData<
	paths["/v1/marketplace/proxies/{proxy_id}"]["patch"]
>;
export type updateOneOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/proxies/{proxy_id}"]["patch"]
>;

export type refreshTokenInput = RequestData<
	paths["/v1/marketplace/refresh"]["post"]
>;
export type refreshTokenOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/refresh"]["post"]
>;

export type userRegisterInput = RequestData<
	paths["/v1/marketplace/register"]["post"]
>;
export type userRegisterOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/register"]["post"]
>;

export type createRelationInput = RequestData<
	paths["/v1/marketplace/relations/"]["post"]
>;
export type createRelationOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/relations/"]["post"]
>;
export type getRelationsInput = RequestData<
	paths["/v1/marketplace/relations/"]["get"]
>;
export type getRelationsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/relations/"]["get"]
>;

export type getAggregatedSellerInput = RequestData<
	paths["/v1/marketplace/relations/sellers/"]["get"]
>;
export type getAggregatedSellerOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/relations/sellers/"]["get"]
>;

export type decideRelationInput = RequestData<
	paths["/v1/marketplace/relations/{relation_id}"]["put"]
>;
export type decideRelationOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/relations/{relation_id}"]["put"]
>;
export type getRelationInput = RequestData<
	paths["/v1/marketplace/relations/{relation_id}"]["get"]
>;
export type getRelationOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/relations/{relation_id}"]["get"]
>;
export type getRelationTransactionsInput = RequestData<
	paths["/v1/marketplace/relations/{relation_id}/transactions"]["get"]
>;
export type getRelationTransactionsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/relations/{relation_id}/transactions"]["get"]
>;
export type updateRelationInput = RequestData<
	paths["/v1/marketplace/relations/{relation_id}"]["patch"]
>;
export type updateRelationOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/relations/{relation_id}"]["patch"]
>;

export type createSenderGroupInput = RequestData<
	paths["/v1/marketplace/sender-groups/"]["post"]
>;
export type createSenderGroupOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/sender-groups/"]["post"]
>;
export type getSenderGroupsInput = RequestData<
	paths["/v1/marketplace/sender-groups/"]["get"]
>;
export type getSenderGroupsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/sender-groups/"]["get"]
>;

export type deleteSenderGroupInput = RequestData<
	paths["/v1/marketplace/sender-groups/{group_id}"]["delete"]
>;
export type deleteSenderGroupOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/sender-groups/{group_id}"]["delete"]
>;
export type getSenderGroupInput = RequestData<
	paths["/v1/marketplace/sender-groups/{group_id}"]["get"]
>;
export type getSenderGroupOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/sender-groups/{group_id}"]["get"]
>;
export type updateSenderGroupInput = RequestData<
	paths["/v1/marketplace/sender-groups/{group_id}"]["patch"]
>;
export type updateSenderGroupOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/sender-groups/{group_id}"]["patch"]
>;

export type createSenderInput = RequestData<
	paths["/v1/marketplace/senders/"]["post"]
>;
export type createSenderOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/"]["post"]
>;
export type getSendersInput = RequestData<
	paths["/v1/marketplace/senders/"]["get"]
>;
export type getSendersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/"]["get"]
>;

export type SenderRowData =
	| components["schemas"]["PersonalSenderOutput"]
	| components["schemas"]["SupplierSenderOutput"];

export type batchCreateSendersInput = RequestData<
	paths["/v1/marketplace/senders/batch/"]["post"]
>;
export type batchCreateSendersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/batch/"]["post"]
>;

export type deleteMultipleSendersInput = RequestData<
	paths["/v1/marketplace/senders/delete-many/"]["post"]
>;
export type deleteMultipleSendersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/delete-many/"]["post"]
>;

export type getSendersFacetsInput = RequestData<
	paths["/v1/marketplace/senders/facets/"]["get"]
>;
export type getSendersFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/facets/"]["get"]
>;

export type requestForBatchReloginInput = RequestData<
	paths["/v1/marketplace/senders/relogin/"]["post"]
>;
export type requestForBatchReloginOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/relogin/"]["post"]
>;

export type stopResumeMultipleSendersInput = RequestData<
	paths["/v1/marketplace/senders/stop-resume/"]["post"]
>;
export type stopResumeMultipleSendersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/stop-resume/"]["post"]
>;

export type getOneSenderInput = RequestData<
	paths["/v1/marketplace/senders/{sender_id}"]["get"]
>;
export type getOneSenderOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/{sender_id}"]["get"]
>;
export type updateSenderInfoInput = RequestData<
	paths["/v1/marketplace/senders/{sender_id}"]["patch"]
>;
export type updateSenderInfoOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/{sender_id}"]["patch"]
>;

export type getSenderTransferListInput = RequestData<
	paths["/v1/marketplace/senders/{sender_id}/transfer-list/"]["get"]
>;
export type getSenderTransferListOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/{sender_id}/transfer-list/"]["get"]
>;
export type getSenderHistoryInput = RequestData<
	paths["/v1/marketplace/senders/{sender_id}/history/"]["get"]
>;
export type getSenderHistoryOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/{sender_id}/history/"]["get"]
>;

export type requestForReloginInput = RequestData<
	paths["/v1/marketplace/senders/{sender_id}/relogin/"]["post"]
>;
export type requestForReloginOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/senders/{sender_id}/relogin/"]["post"]
>;

export type getSnipeTransferPlayerSuggestionsInput = RequestData<
	paths["/v1/marketplace/snipe-transfer-player-suggestions/"]["get"]
>;
export type getSnipeTransferPlayerSuggestionsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/snipe-transfer-player-suggestions/"]["get"]
>;

export type getSnipeTransferPlayerSuggestionsFacetsInput = RequestData<
	paths["/v1/marketplace/snipe-transfer-player-suggestions/facets/"]["get"]
>;
export type getSnipeTransferPlayerSuggestionsFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/snipe-transfer-player-suggestions/facets/"]["get"]
>;

export type readAllPlayersInput = RequestData<
	paths["/v1/marketplace/snipe-transfer-players/"]["get"]
>;
export type readAllPlayersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/snipe-transfer-players/"]["get"]
>;

export type getTransferPlayersFacetsInput = RequestData<
	paths["/v1/marketplace/snipe-transfer-players/facets/"]["get"]
>;
export type getTransferPlayersFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/snipe-transfer-players/facets/"]["get"]
>;

export type deletePlayerInput = RequestData<
	paths["/v1/marketplace/snipe-transfer-players/{transfer_player_id}"]["delete"]
>;
export type deletePlayerOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/snipe-transfer-players/{transfer_player_id}"]["delete"]
>;
export type updatePlayerInput = RequestData<
	paths["/v1/marketplace/snipe-transfer-players/{transfer_player_id}"]["patch"]
>;
export type updatePlayerOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/snipe-transfer-players/{transfer_player_id}"]["patch"]
>;

export type getStatusInformationInput = RequestData<
	paths["/v1/marketplace/status/"]["get"]
>;
export type getStatusInformationOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/status/"]["get"]
>;

export type getAllTransferRecordsInput = RequestData<
	paths["/v1/marketplace/transfer-records/"]["get"]
>;
export type getAllTransferRecordsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/transfer-records/"]["get"]
>;
export type getTransferRecordsFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/transfer-records/facets/"]["get"]
>;
export type getTransferRecordsPlayerFacetsInput = RequestData<
	paths["/v1/marketplace/transfer-records/facets/players/"]["get"]
>;
export type getTransferRecordsPlayerFacetsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/transfer-records/facets/players/"]["get"]
>;

export type getIdFromNameInput = RequestData<
	paths["/v1/marketplace/users/name-lookup"]["get"]
>;
export type getIdFromNameOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/users/name-lookup"]["get"]
>;

export type getUserProfileInput = RequestData<
	paths["/v1/marketplace/users/{user_id}"]["get"]
>;
export type getUserProfileOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/users/{user_id}"]["get"]
>;

export type getUserPaymentInformationInput = RequestData<
	paths["/v1/marketplace/users/{user_id}/payment-info"]["get"]
>;
export type getUserPaymentInformationOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/users/{user_id}/payment-info"]["get"]
>;

// ----------------- Banner endpoints -----------------
export type getActiveBannersInput = RequestData<
	paths["/v1/marketplace/banner/"]["get"]
>;
export type getActiveBannersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/banner/"]["get"]
>;

export type getGenericBannersInput = RequestData<
	paths["/v1/marketplace/banner/generic"]["get"]
>;
export type getGenericBannersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/banner/generic"]["get"]
>;

export type createBannerInput = RequestData<
	paths["/v1/marketplace/banner/"]["post"]
>;
export type createBannerOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/banner/"]["post"]
>;

export type deleteBannerInput = RequestData<
	paths["/v1/marketplace/banner/{banner_id}"]["delete"]
>;
export type deleteBannerOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/banner/{banner_id}"]["delete"]
>;

export type updateBannerInput = RequestData<
	paths["/v1/marketplace/banner/{banner_id}"]["patch"]
>;
export type updateBannerOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/banner/{banner_id}"]["patch"]
>;

export type acknowledgeBannerInput = RequestData<
	paths["/v1/marketplace/banner/{banner_id}/acknowledge"]["post"]
>;
export type acknowledgeBannerOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/banner/{banner_id}/acknowledge"]["post"]
>;

// ----------------- Dashboard common (operationId clashed with generator) -----------------
export type commonInput = RequestData<
	paths["/v1/marketplace/dashboard/common/"]["get"]
>;
export type commonOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/dashboard/common/"]["get"]
>;

// ----------------- App-specific derived types (folded from former api/manual.ts) -----------------
export type OrderAdminOutput = components["schemas"]["OrderAdminOutput"];
export type OrderSellerOutput = components["schemas"]["OrderSellerOutput"];
export type SenderStatusSchema = components["schemas"]["SenderStatusSchema"];
export type HTTPValidationError = components["schemas"]["HTTPValidationError"];

export type OrderStatus = OrderBuyerOutput["status"];
export type SenderStatus = SenderStatusSchema["status"];

export type GeneralApiError =
	| {
			detail?: string;
			type?: string;
	  }
	| HTTPValidationError;

export const ACTIVE_OR_BANNED = [
	"ACTIVE",
	"BANNED",
] as const satisfies RelationQuery[];

export type ActiveOrBanned = (typeof ACTIVE_OR_BANNED)[number];

/** An order created with a customer link whose customer hasn't submitted an EA account yet */
export type OrderEndCustomerUnclaimedOutput =
	components["schemas"]["OrderEndCustomerUnclaimedOutput"];

export type OrderUnifiedOutput =
	| OrderAdminOutput
	| OrderBuyerOutput
	| OrderSellerOutput
	| OrderEndCustomerUnclaimedOutput;

export type ResolvedToKeep =
	| components["schemas"]["SenderToKeep"]
	| components["schemas"]["SenderGroupToKeep"]
	| components["schemas"]["GlobalToKeep"];

export type ResolvedPriority =
	| components["schemas"]["SenderPriority"]
	| components["schemas"]["SenderGroupPriority"]
	| components["schemas"]["GlobalPriority"];

// ----------------- Admin dashboard, tickets, commission -----------------
export type AdminDashboardOutput =
	components["schemas"]["AdminDashboardOutput"];
export type SpeedAnalysis = components["schemas"]["SpeedAnalysis"];
export type TicketOutput = components["schemas"]["TicketOutput"];
export type TicketStatus = components["schemas"]["TicketStatus"];
export type CommissionSummary = components["schemas"]["CommissionSummary"];

export type getAdminDashboardInput = RequestData<
	paths["/v1/marketplace/admin/dashboard/"]["get"]
>;
export type getAdminDashboardOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/dashboard/"]["get"]
>;
export type getTicketsInput = RequestData<
	paths["/v1/marketplace/admin/tickets/"]["get"]
>;
export type getTicketsOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/tickets/"]["get"]
>;
export type getTicketsCountOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/tickets/count/"]["get"]
>;
export type updateTicketInput = RequestData<
	paths["/v1/marketplace/admin/tickets/{ticket_id}"]["patch"]
>;
export type updateTicketOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/tickets/{ticket_id}"]["patch"]
>;
export type getSenderOwnersInput = RequestData<
	paths["/v1/marketplace/admin/sender-owners/"]["get"]
>;
export type getSenderOwnersOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/sender-owners/"]["get"]
>;
export type getCommissionSummaryOutput = ResponseSuccessJSON<
	paths["/v1/marketplace/admin/commission-summary/"]["get"]
>;
